import type { DataSource, EntityCollection } from "@firecms/core";
import type { ContentScopeId } from "../workspace/collectionIds";
import { normalizeImportItem } from "./normalizeImportItem";
import {
  loadReferenceMaps,
  resolveImportReferences,
  type ReferenceMaps,
} from "./resolveReferences";
import type {
  ContentImportCollection,
  ContentImportItemDto,
  ContentImportResult,
} from "./types";
import {
  importItemKey,
  validateImportItem,
} from "./validateImportItem";

async function fetchExistingEntities(
  dataSource: DataSource,
  path: string,
  collection: EntityCollection
) {
  return dataSource.fetchCollection({
    path,
    collection,
  });
}

function buildExistingIdByCode(
  entities: Awaited<ReturnType<typeof fetchExistingEntities>>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const entity of entities) {
    const code = entity.values?.code;
    if (typeof code === "string") {
      map.set(code, entity.id);
    }
  }
  return map;
}

function resolveEntityIdByCode(
  item: ContentImportItemDto,
  existingByCode: Map<string, string>
): { entityId?: string; status: "new" | "existing" } {
  const code = item.code;
  if (typeof code !== "string") {
    return { status: "new" };
  }

  const existingId = existingByCode.get(code);
  if (existingId) {
    return { entityId: existingId, status: "existing" };
  }

  return { status: "new" };
}

function updateReferenceMapsAfterSave(
  importCollection: ContentImportCollection,
  normalized: ContentImportItemDto,
  savedId: string,
  maps: ReferenceMaps,
  existingByCode: Map<string, string>
): void {
  const code = normalized.code;
  if (typeof code !== "string") {
    return;
  }

  existingByCode.set(code, savedId);

  switch (importCollection) {
    case "units":
      maps.unitByCode.set(code, savedId);
      break;
    case "stories":
      maps.storyByCode.set(code, savedId);
      break;
    case "levels":
      maps.levelByCode.set(code, savedId);
      break;
  }
}

export async function upsertContentItems(params: {
  dataSource: DataSource;
  collection: EntityCollection;
  importCollection: ContentImportCollection;
  path: string;
  contentScopeId: ContentScopeId;
  items: ContentImportItemDto[];
}): Promise<ContentImportResult> {
  const {
    dataSource,
    collection,
    importCollection,
    path,
    contentScopeId,
    items,
  } = params;

  const referenceMaps = await loadReferenceMaps(dataSource, contentScopeId);
  const existingEntities = await fetchExistingEntities(
    dataSource,
    path,
    collection
  );
  const existingByCode = buildExistingIdByCode(existingEntities);

  const result: ContentImportResult = { imported: 0, failed: [] };

  for (let index = 0; index < items.length; index++) {
    const raw = items[index];
    if (!raw) {
      continue;
    }
    const key = importItemKey(importCollection, raw);

    const validation = validateImportItem(importCollection, raw);
    if (!validation.ok) {
      result.failed.push({ index, key, reason: validation.reason });
      continue;
    }

    const normalized = normalizeImportItem(importCollection, raw);
    const resolved = resolveImportReferences(
      importCollection,
      normalized,
      contentScopeId,
      referenceMaps
    );
    if ("error" in resolved) {
      result.failed.push({ index, key, reason: resolved.error });
      continue;
    }

    try {
      const { entityId, status } = resolveEntityIdByCode(
        normalized,
        existingByCode
      );

      const saved = await dataSource.saveEntity({
        path,
        collection,
        values: resolved.values,
        status,
        ...(entityId ? { entityId } : {}),
      });

      const savedId = saved?.id ?? entityId;
      if (!savedId) {
        result.failed.push({
          index,
          key,
          reason: "Save succeeded but no entity id returned",
        });
        continue;
      }

      updateReferenceMapsAfterSave(
        importCollection,
        normalized,
        savedId,
        referenceMaps,
        existingByCode
      );

      result.imported += 1;
    } catch (error: unknown) {
      const reason =
        error instanceof Error ? error.message : "Unknown save error";
      result.failed.push({ index, key, reason });
    }
  }

  return result;
}
